import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import React, { useMemo } from 'react';
import { echartCountMat, getPercentageInfo } from '@/pages/opinion/utils';
import { useIntl } from 'umi';
import { formatTimeXAxisText } from '@/pages/opinion/utils/chart';
import { useGetCharData } from '../../hooks/useGetCharData';
import { reviewsFormat, yAxleFormat } from '@/pages/opinion/utils/value';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import useGameChartRich from '../../hooks/useGameChartRich';

export default function LineChart({
  xAxisList,
  data,
  filter,
  mentionTab,
  loading,
  height,
  leftTitle,
  formatValue = reviewsFormat,
  hasShare = true,
  title = 'Mentions',
  yAxisOptions = {},
}: any) {
  const { formatMessage } = useIntl();
  const { legendData, chartData, timeData } = useGetCharData(
    data,
    filter,
    xAxisList?.map((item: any) => item?.edition_unified_id),
    'line',
  );

  const { nameMap } = useGameChartRich({ xAxisList });

  const options = useMemo(() => {
    const option = {
      tooltip: {
        formatter: (param: any) => {
          let { name } = param?.[0] || {};
          return formatChartParamsDate({
            params: param.sort((a: any, b: any) => b.value - a.value),
            listTitleFunc: (item) => nameMap[item.seriesName],
            valueFormat: (value) => formatValue(value),
            dealwithRecords: (records: Array<any>, total: number) => {
              return records.map((item) => {
                return {
                  ...item,
                  share: getPercentageInfo(item.originValue, total, 2),
                };
              });
            },
            tablesConfigFunc: (records: Array<any>) => [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    header: listTimeTitleFunc(name, filter.date_type),
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: title }),
                  },
                  ...(hasShare
                    ? [
                        {
                          key: 'share',
                          align: 'right',
                          weight: 'bold',
                          header: formatMessage({ id: 'Share-1' }),
                        },
                      ]
                    : []),
                ],
              },
            ],
          });
        },
      },
      legend: {
        data: legendData,
        formatter: function (name: any) {
          return nameMap[name];
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter.date_type),
      ),
      xAxis: {
        type: 'category',
        axisLabel: {
          formatter: (value: string) =>
            formatTimeXAxisText(value, filter.date_type),
        },
        boundaryGap: ['20%', 0],
        data: timeData,
      },
      yAxis: {
        type: 'value',
        ...yAxisOptions,
        axisLabel: {
          formatter: (value: any) => {
            return mentionTab === 'mentions_dau'
              ? echartCountMat(value * 10000) + '‱'
              : yAxleFormat(value);
          },
        },
      },
      series: chartData,
    };

    return option;
  }, [legendData, chartData, timeData, formatMessage, nameMap, title]);

  return (
    <EChartCommon
      options={options}
      loading={loading}
      leftTitle={leftTitle}
      height={height}
      optionsProps={{ type: 'line' }}
      empty={data?.length === 0}
    ></EChartCommon>
  );
}
